import { ComponentProps, FC } from "react";
import { NavLink, Outlet } from "react-router";
import { Badge, Text } from "@postenbring/hedwig-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse } from "@fortawesome/pro-solid-svg-icons/faHouse";
import { faCircleCheck } from "@fortawesome/pro-solid-svg-icons/faCircleCheck";
import { faLock } from "@fortawesome/pro-solid-svg-icons/faLock";
import { faClockRotateLeft } from "@fortawesome/pro-solid-svg-icons/faClockRotateLeft";
import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import Translated from "../infrastructure/translation/Translated";
import { TranslationKey } from "../infrastructure/translation/translator";
import { useSelectedOrganization } from "../infrastructure/organization/OrganizationContext";
import styles from "./OrganizationLayout.module.css";

interface MenuItem {
  to: string;
  labelKey: TranslationKey;
  icon: ComponentProps<typeof FontAwesomeIcon>["icon"];
  end?: boolean;
}

const MenuLink: FC<{ item: MenuItem }> = ({ item }) => (
  <NavLink
    to={item.to}
    end={item.end}
    className={({ isActive }) =>
      isActive ? `${styles.menuItem} ${styles.menuItemActive}` : styles.menuItem
    }
  >
    <FontAwesomeIcon icon={item.icon} className={styles.menuIcon} />
    <Translated textKey={item.labelKey} />
  </NavLink>
);

export const OrganizationLayout: FC = () => {
  const organization = useSelectedOrganization();

  const menuItems: MenuItem[] = [
    {
      to: ".",
      labelKey: "home.header",
      icon: faHouse,
      end: true,
    },
    {
      to: "clients",
      labelKey: "dashboard.clients",
      icon: faCircleCheck,
    },
    {
      to: "certificates",
      labelKey: "dashboard.certificates",
      icon: faLock,
    },
    {
      to: "users",
      labelKey: "users.header",
      icon: faUsers,
    },
    {
      to: "auditlog",
      labelKey: "auditlog.header",
      icon: faClockRotateLeft,
    },
  ];

  return (
    <div className={styles.container}>
      <nav className={styles.menu} aria-label="Organization navigation">
        {menuItems.map((item) => (
          <MenuLink key={item.to} item={item} />
        ))}
      </nav>
      <main className={styles.layout}>
        <div className={styles.pageHeader}>
          <Text variant="h1" as="h1" size="min">
            {organization.organization.name}
          </Text>
          <p className={styles.orgnr}>
            <Translated textKey={"organizations.orgnr"} />
            <Badge data-color="neutral">
              {organization.organization.orgnr}
            </Badge>
          </p>
        </div>

        <div className={styles.content}>
          <Outlet />
        </div>
      </main>
    </div>
  );
};
